import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./stage1Escape.css";
import "./stage2Escape.css";
import { fireGameStagePopup } from "../../functions/gameStagePopup";
import Stage2CalendarOptimize from "./Stage2CalendarOptimize";
import Stage2ChannelChoice from "./Stage2ChannelChoice";
import Stage2HiddenPrinciples from "./Stage2HiddenPrinciples";
import Stage2FinalComplete from "./Stage2FinalComplete";
import { STAGE2_CALENDAR_PUZZLE_POINTS } from "./stage2CalendarData";
import { STAGE2_CHANNEL_PUZZLE_POINTS } from "./stage2ChannelData";
import {
  STAGE2_PRINCIPLES_PUZZLE_POINTS,
  STRESS_AFTER_CALENDAR,
  STRESS_AFTER_CHANNEL,
  STRESS_BALANCED,
  STRESS_START,
} from "./stage2PrinciplesData";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  isLevelScoreRecorded,
  LEVELS_BY_ADVENTURE,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  sumLevelScores,
  updateExternalReport,
} from "../../functions/stageReportSync";
import {
  showStageCompleteCelebration,
  navigateAfterCelebration,
} from "../../functions/stageCelebration";
import stage2Desk from "./img/stage2.png";
import stage2Mob from "./img/stage2_mob.png";
import finalDesk from "./img/final.png";
import finalMob from "./img/final_mob.png";
import {
  applyStageBodyBackground,
  isMobViewport,
} from "./gameStageBackground";
import StageTimer from "./StageTimer";
import StageHelpButton from "./StageHelpButton";
import { fireEscapeHowToPlay } from "../../functions/escapeGrindHowToPlay";

const STAGE_NUM = 2;
const STAGE2_MEETING_ROOM_KEY = "stage2EnterMeetingRoom";

const GAME_LEVEL = {
  CALENDAR: 4,
  CHANNEL: 5,
  PRINCIPLES: 6,
};

function stressForStageRecord(stageRecord) {
  if (!stageRecord) return STRESS_START;
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.PRINCIPLES)) {
    return STRESS_BALANCED;
  }
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.CHANNEL)) {
    return STRESS_AFTER_CHANNEL;
  }
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.CALENDAR)) {
    return STRESS_AFTER_CALENDAR;
  }
  return STRESS_START;
}

const PHASE = {
  LOADING: "loading",
  WELCOME: "welcome",
  PUZZLE1_INTRO: "puzzle1_intro",
  PUZZLE1: "puzzle1",
  CALENDAR_LEARNING: "calendar_learning",
  PUZZLE2_INTRO: "puzzle2_intro",
  PUZZLE2: "puzzle2",
  CHANNEL_LEARNING: "channel_learning",
  PUZZLE3_INTRO: "puzzle3_intro",
  PUZZLE3: "puzzle3",
  PRINCIPLES_LEARNING: "principles_learning",
  FINAL_COMPLETE: "final_complete",
};

const FINAL_ROOM_PHASES = new Set([
  PHASE.PUZZLE3_INTRO,
  PHASE.PUZZLE3,
  PHASE.PRINCIPLES_LEARNING,
  PHASE.FINAL_COMPLETE,
]);

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function StressMeter({ value, variant = "vertical", elevated = false }) {
  const isHorizontal = variant === "horizontal";
  const isBalanced = value <= STRESS_BALANCED + 4;

  return (
    <div
      className={`stage1-stress stage1-stress--${variant}${
        elevated ? " stage1-stress--elevated" : ""
      }${isBalanced ? " stage1-stress--balanced" : ""}`}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={isBalanced ? "Stress meter balanced" : `Stress meter ${value} percent`}
    >
      <span className="stage1-stress__heading">Stress Meter</span>
      <div className="stage1-stress__track">
        <div
          className="stage1-stress__fill"
          style={
            isHorizontal ? { width: `${value}%` } : { height: `${value}%` }
          }
        />
      </div>
      <span className="stage1-stress__value">
        {isBalanced ? "Balanced" : `${value}%`}
      </span>
    </div>
  );
}

function MeetingMazeIntroModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay stage2-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="maze-title">
      <div className="stage1-modal">
        <h2 id="maze-title" className="stage1-modal__title stage2-modal__title">
          The Meeting Maze
        </h2>
        <p className="stage1-modal__time">Level 2 — Meeting Room</p>
        <div className="stage1-modal__body">
          <p>
            Your calendar is packed — back-to-back meetings from morning until
            evening. No breaks. No focus time. Just one invite after another.
          </p>
          <p>
            Somewhere in this maze is room to breathe, think, and actually get
            work done. It&apos;s time to take your schedule back.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Fix Your Calendar
        </button>
      </div>
    </div>
  );
}

function ProtectTimeModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay stage2-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="protect-title">
      <div className="stage1-modal">
        <h2 id="protect-title" className="stage1-modal__title stage2-modal__title">
          Protect Your Time
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            A calendar full of meetings isn&apos;t a badge of productivity — it&apos;s
            a wall between you and the work that matters.
          </p>
          <p>
            When you shorten meetings, add breaks, and block focus time, you
            create space for deep work instead of just talking about it.
          </p>
          <p className="stage2-modal__takeaway">
            Key takeaway: A productive calendar creates space for work, not just
            meetings.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function AnotherMeetingIntroModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay stage2-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="another-title">
      <div className="stage1-modal">
        <h2 id="another-title" className="stage1-modal__title stage2-modal__title">
          Another Meeting?
        </h2>
        <p className="stage1-modal__time">Level 2 — Communication</p>
        <div className="stage1-modal__body">
          <p>
            Every request seems to become a meeting. A quick question? Schedule a
            call. A simple update? Book 30 minutes. Before you know it, your
            day disappears into other people&apos;s agendas.
          </p>
          <p>
            Not every message needs a calendar invite. Choose the right channel
            and reclaim your time.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Choose Wisely
        </button>
      </div>
    </div>
  );
}

function ChannelLearningModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay stage2-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="channel-title">
      <div className="stage1-modal">
        <h2 id="channel-title" className="stage1-modal__title stage2-modal__title">
          Choose the Right Channel
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            Meetings, emails, and chat each have a place — but defaulting to
            meetings for everything is how calendars get crushed.
          </p>
          <p>
            Ask yourself: Does this need a live discussion, a documented record,
            or just a quick back-and-forth?
          </p>
          <p className="stage2-modal__takeaway">
            Key takeaway: The best communication method is the simplest one that
            gets the job done.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function PrinciplesIntroModal({ onContinue }) {
  return (
    <div
      className="stage1-modal-overlay stage2-modal-overlay stage2-modal-overlay--final"
      role="dialog"
      aria-modal="true"
      aria-labelledby="principles-intro-title"
    >
      <div className="stage1-modal">
        <h2 id="principles-intro-title" className="stage1-modal__title stage2-modal__title">
          The Missing Principles
        </h2>
        <p className="stage1-modal__time">Final Challenge</p>
        <div className="stage1-modal__body">
          <p>
            You&apos;ve tamed your calendar and chosen the right channels — but
            something still feels off. Five productivity principles are hidden
            around this room, woven into the space where you work.
          </p>
          <p>
            Find <strong>PRIORITIZE</strong>, <strong>PLAN</strong>,{" "}
            <strong>FOCUS</strong>, <strong>REST</strong>, and{" "}
            <strong>BALANCE</strong> hidden around the room.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Search the Room
        </button>
      </div>
    </div>
  );
}

function PrinciplesLearningModal({ onContinue }) {
  return (
    <div
      className="stage1-modal-overlay stage2-modal-overlay stage2-modal-overlay--final"
      role="dialog"
      aria-modal="true"
      aria-labelledby="framework-title"
    >
      <div className="stage1-modal">
        <h2 id="framework-title" className="stage1-modal__title stage2-modal__title">
          The Productivity Framework
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            Productivity isn&apos;t about doing more — it&apos;s about doing what
            matters, with intention, and recovering so you can sustain it.
          </p>
          <p>
            Prioritize first. Plan with purpose. Focus deeply. Rest deliberately.
            Stay balanced.
          </p>
          <p className="stage2-modal__takeaway">
            Key takeaway: Work smarter. Recover better. Stay balanced.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

export default function Stage2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage2Points, setStage2Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(isMobViewport);
  const [stressLevel, setStressLevel] = useState(STRESS_START);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const welcomeShownRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const isPuzzlePhase =
    phase === PHASE.PUZZLE1 ||
    phase === PHASE.PUZZLE2 ||
    phase === PHASE.PUZZLE3;
  const useFinalRoomBg = FINAL_ROOM_PHASES.has(phase);

  const showStressMeter =
    phase !== PHASE.LOADING &&
    phase !== PHASE.FINAL_COMPLETE &&
    (phase !== PHASE.PUZZLE3 || isMobilePortrait);

  const stressMeterElevated =
    phase === PHASE.PUZZLE1_INTRO ||
    phase === PHASE.CALENDAR_LEARNING ||
    phase === PHASE.PUZZLE2_INTRO ||
    phase === PHASE.CHANNEL_LEARNING ||
    phase === PHASE.PUZZLE3_INTRO ||
    phase === PHASE.PRINCIPLES_LEARNING;

  useEffect(() => {
    const syncViewport = () => setIsMobilePortrait(isMobViewport());
    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    return () => {
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("stage2-escape-page");
    if (isPuzzlePhase) {
      document.body.classList.add("stage2-puzzle-active");
    } else {
      document.body.classList.remove("stage2-puzzle-active");
    }

    const html = document.documentElement;
    const body = document.body;
    const bgKeys = [
      "background-image",
      "background-size",
      "background-position",
      "background-repeat",
      "background-attachment",
      "background-color",
      "min-height",
    ];
    const bodySnapshot = bgKeys.map((k) => [k, body.style.getPropertyValue(k)]);
    const htmlMinHeight = html.style.minHeight;

    const applyBackground = () => {
      const mob = isMobViewport();
      const bg = useFinalRoomBg
        ? mob
          ? finalMob
          : finalDesk
        : mob
          ? stage2Mob
          : stage2Desk;
      applyStageBodyBackground(body, { foregroundUrl: bg, mob });
    };

    html.style.minHeight = "100%";
    body.style.minHeight = "100dvh";
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);

    return () => {
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      bodySnapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
      if (htmlMinHeight) html.style.minHeight = htmlMinHeight;
      else html.style.removeProperty("min-height");
      document.body.classList.remove("stage2-escape-page");
      document.body.classList.remove("stage2-puzzle-active");
    };
  }, [isPuzzlePhase, useFinalRoomBg]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage2Points);
  }, [stage2Points]);

  useEffect(() => {
    if (!dbReady || welcomeShownRef.current) return;

    if (sessionStorage.getItem(STAGE2_MEETING_ROOM_KEY) === "1") {
      sessionStorage.removeItem(STAGE2_MEETING_ROOM_KEY);
      welcomeShownRef.current = true;
      setPhase(PHASE.WELCOME);
      fireGameStagePopup({
        title: "The Meeting Room",
        html: "<p>You've earned your seat at the table. Your calendar — and the meetings inside it — are up next.</p>",
        icon: "info",
        confirmButtonText: "Let's Go",
        themeData,
      }).then(() => setPhase(PHASE.PUZZLE1_INTRO));
    } else {
      welcomeShownRef.current = true;
      setPhase(PHASE.PUZZLE1_INTRO);
    }
  }, [dbReady, themeData]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded") return;

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage2Sum = sumLevelScores(st, LEVELS_BY_ADVENTURE[STAGE_NUM]);

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage2Sum);
          setStage2Points(dbStage2Sum);
          setDisplayTotalScore(dbTotal);

          let resumePhase = null;
          if (isLevelScoreRecorded(st, GAME_LEVEL.PRINCIPLES)) {
            resumePhase = PHASE.FINAL_COMPLETE;
          } else if (isLevelScoreRecorded(st, GAME_LEVEL.CHANNEL)) {
            resumePhase = PHASE.PUZZLE3_INTRO;
          } else if (isLevelScoreRecorded(st, GAME_LEVEL.CALENDAR)) {
            resumePhase = PHASE.PUZZLE2_INTRO;
          }

          setStressLevel(stressForStageRecord(st));

          setStageActive(STAGE_NUM, storedUser);
          if (!cancelled) {
            if (resumePhase) {
              welcomeShownRef.current = true;
              setPhase(resumePhase);
            }
            setDbReady(true);
          }
          return;
        }

        setStageActive(STAGE_NUM, storedUser);
        if (!cancelled) setDbReady(true);
      } catch (err) {
        console.error("Stage2 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [themeStatus, backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const saveLevelProgress = useCallback(
    async (levelNumber, score, answers, adventureScoreSoFar) => {
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
      if (isDemoBypass || !backendBase || !adminToken || !stageId) return null;

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const total_score = otherStagesTotalRef.current + adventureScoreSoFar;

      const puzzleType =
        levelNumber === GAME_LEVEL.CALENDAR
          ? "calendar"
          : levelNumber === GAME_LEVEL.CHANNEL
            ? "channel"
            : "principles";

      const saveResult = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: levelNumber,
        score,
        answers: {
          level: levelNumber,
          puzzle: puzzleType,
          data: answers,
        },
        time: timeStr,
        total_score,
      });

      if (saveResult.total_score != null) {
        setDisplayTotalScore(saveResult.total_score);
      }
      return saveResult;
    },
    [isDemoBypass, backendBase, adminToken, storedUser]
  );

  const finishStage = useCallback(
    async (score, { skipCelebration = false } = {}) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        try {
          const uid = storedUser.userId || storedUser.userid || storedUser.id;
          const sessionId = storedUser.sessionId;
          const organizationId = storedUser.organizationId;

          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            time: timeStr,
            total_score: finalTotal,
          });
          if (saveResult.total_score != null) {
            setDisplayTotalScore(saveResult.total_score);
          }
          await fetch(`${backendBase}/welcomeStageComplete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId,
              time: timeStr,
              total_score: finalTotal,
              points: finalTotal,
            }),
          });
          await updateExternalReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            points: finalTotal,
            time: timeStr,
            stageId,
          });
        } catch (err) {
          console.error("Stage2 finish:", err);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (!skipCelebration) {
        await showStageCompleteCelebration({
          stageNumber: STAGE_NUM,
          skip: false,
          score,
        });
      }

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigateAfterCelebration(navigate, "/leaderboard");
    },
    [submitting, storedUser, isDemoBypass, backendBase, adminToken, navigate]
  );

  const stage2FullScore =
    STAGE2_CALENDAR_PUZZLE_POINTS +
    STAGE2_CHANNEL_PUZZLE_POINTS +
    STAGE2_PRINCIPLES_PUZZLE_POINTS;

  const handlePuzzle1Success = useCallback(
    async (schedule) => {
      const adventureScore = STAGE2_CALENDAR_PUZZLE_POINTS;
      setStage2Points(adventureScore);
      await saveLevelProgress(
        GAME_LEVEL.CALENDAR,
        STAGE2_CALENDAR_PUZZLE_POINTS,
        schedule,
        adventureScore
      );
      await fireGameStagePopup({
        title: "Calendar Optimized!",
        html: `<p>Your schedule now has room for focus, breaks, and what matters most.</p><p><strong>+${STAGE2_CALENDAR_PUZZLE_POINTS} points</strong></p>`,
        icon: "success",
        confirmButtonText: "Continue",
        themeData,
      });
      setStressLevel(STRESS_AFTER_CALENDAR);
      setPhase(PHASE.CALENDAR_LEARNING);
    },
    [themeData, saveLevelProgress]
  );

  const handlePuzzle2Success = useCallback(
    async (choices) => {
      const totalScore = STAGE2_CALENDAR_PUZZLE_POINTS + STAGE2_CHANNEL_PUZZLE_POINTS;
      setStage2Points(totalScore);
      await saveLevelProgress(
        GAME_LEVEL.CHANNEL,
        STAGE2_CHANNEL_PUZZLE_POINTS,
        choices,
        totalScore
      );
      await fireGameStagePopup({
        title: "Great Choices!",
        html: `<p>You matched each situation to the right communication channel.</p><p><strong>+${STAGE2_CHANNEL_PUZZLE_POINTS} points</strong></p>`,
        icon: "success",
        confirmButtonText: "Continue",
        themeData,
      });
      setStressLevel(STRESS_AFTER_CHANNEL);
      setPhase(PHASE.CHANNEL_LEARNING);
    },
    [themeData, saveLevelProgress]
  );

  const handleChannelLearningDone = useCallback(() => {
    setPhase(PHASE.PUZZLE3_INTRO);
  }, []);

  const handlePuzzle3Success = useCallback(
    async (answers) => {
      setStage2Points(stage2FullScore);
      await saveLevelProgress(
        GAME_LEVEL.PRINCIPLES,
        STAGE2_PRINCIPLES_PUZZLE_POINTS,
        answers,
        stage2FullScore
      );
      setStressLevel(STRESS_BALANCED);
      setPhase(PHASE.PRINCIPLES_LEARNING);
    },
    [saveLevelProgress, stage2FullScore]
  );

  const handlePrinciplesLearningDone = useCallback(() => {
    setPhase(PHASE.FINAL_COMPLETE);
  }, []);

  const handleFinalContinue = useCallback(async () => {
    await finishStage(stage2FullScore, { skipCelebration: true });
  }, [finishStage, stage2FullScore]);

  const helpPuzzleKey = useMemo(() => {
    if (phase === PHASE.PUZZLE1) return "calendar";
    if (phase === PHASE.PUZZLE2) return "channel";
    if (phase === PHASE.PUZZLE3) return "principles";
    return null;
  }, [phase]);

  const showHelp = Boolean(helpPuzzleKey);

  const handleHowToPlay = useCallback(() => {
    if (!helpPuzzleKey) return;
    fireEscapeHowToPlay({
      stageNumber: STAGE_NUM,
      puzzleKey: helpPuzzleKey,
      themeData,
    });
  }, [helpPuzzleKey, themeData]);

  if (phase === PHASE.LOADING || !dbReady) {
    return (
      <div className="stage2-escape">
        <p className="stage2-escape__loading">Loading game…</p>
      </div>
    );
  }

  return (
    <div className={`stage2-escape${isPuzzlePhase ? " stage2-escape--puzzle" : ""}`}>
      {phase !== PHASE.FINAL_COMPLETE ? (
        <div className="stage-escape-hud">
          {showHelp ? <StageHelpButton onClick={handleHowToPlay} /> : null}
          <StageTimer timeLabel={formatSecondsToClock(elapsedSec)} />
        </div>
      ) : null}

      {showStressMeter ? (
        <StressMeter
          value={stressLevel}
          variant={isMobilePortrait ? "horizontal" : "vertical"}
          elevated={stressMeterElevated}
        />
      ) : null}

      {phase === PHASE.PUZZLE1_INTRO ? (
        <MeetingMazeIntroModal onContinue={() => setPhase(PHASE.PUZZLE1)} />
      ) : null}

      {phase === PHASE.PUZZLE1 ? (
        <div className="stage2-escape__puzzle-shell">
          <Stage2CalendarOptimize
            onSubmit={handlePuzzle1Success}
            submitting={submitting}
          />
        </div>
      ) : null}

      {phase === PHASE.CALENDAR_LEARNING ? (
        <ProtectTimeModal onContinue={() => setPhase(PHASE.PUZZLE2_INTRO)} />
      ) : null}

      {phase === PHASE.PUZZLE2_INTRO ? (
        <AnotherMeetingIntroModal onContinue={() => setPhase(PHASE.PUZZLE2)} />
      ) : null}

      {phase === PHASE.PUZZLE2 ? (
        <div className="stage2-escape__puzzle-shell">
          <Stage2ChannelChoice
            onComplete={handlePuzzle2Success}
            submitting={submitting}
            themeData={themeData}
          />
        </div>
      ) : null}

      {phase === PHASE.CHANNEL_LEARNING ? (
        <ChannelLearningModal onContinue={handleChannelLearningDone} />
      ) : null}

      {phase === PHASE.PUZZLE3_INTRO ? (
        <PrinciplesIntroModal onContinue={() => setPhase(PHASE.PUZZLE3)} />
      ) : null}

      {phase === PHASE.PUZZLE3 ? (
        <div className="stage2-escape__puzzle-shell stage2-escape__puzzle-shell--final">
              <Stage2HiddenPrinciples
                onComplete={handlePuzzle3Success}
                stress={stressLevel}
                onStressChange={setStressLevel}
                showInternalStress={!isMobilePortrait}
                submitting={submitting}
                themeData={themeData}
              />
        </div>
      ) : null}

      {phase === PHASE.PRINCIPLES_LEARNING ? (
        <PrinciplesLearningModal onContinue={handlePrinciplesLearningDone} />
      ) : null}

      {phase === PHASE.FINAL_COMPLETE ? (
        <Stage2FinalComplete
          productivityScore={displayTotalScore}
          onContinue={handleFinalContinue}
          submitting={submitting}
        />
      ) : null}
    </div>
  );
}
